"use client";

import { useState } from "react";
import Image from "next/image";
import CustomButton from "../CustomButton";
import { useGetDivisionsQuery } from "@/redux/features/divisions/divisionsApi";
import { TDivision, DivisionType } from "@/types";

export interface Service {
  id: string;
  name: string;
  description: string;
  theme: "counseling" | "lifeCoaching" | "occupationalTherapy" | "psychology";
  image?: string;
  divisionId?: string; // Backend division ID
  divisionType?: DivisionType; // Backend division type
}

interface ServiceSelectionProps {
  title?: string;
  subtitle?: string;
  services?: Service[];
  defaultSelectedId?: string;
  onServiceSelect?: (service: Service) => void;
  onContinue?: (selectedService: Service | null) => void;
}

const themeConfig = {
  counseling: {
    borderColor: "border-[#896deb]",
    gradientFrom: "from-transparent",
    gradientTo: "to-[#896deb]",
    shadowColor: "shadow-[0px_0px_0px_8px_rgba(137,109,235,0.3)]",
  },
  lifeCoaching: {
    borderColor: "border-[#DDA500]",
    gradientFrom: "from-transparent",
    gradientTo: "to-[#DDA500]",
    shadowColor: "shadow-[0px_0px_0px_8px_rgba(221,165,0,0.3)]",
  },
  occupationalTherapy: {
    borderColor: "border-red",
    gradientFrom: "from-transparent",
    gradientTo: "to-red",
    shadowColor: "shadow-[0px_0px_0px_8px_rgba(192,22,16,0.3)]",
  },
  psychology: {
    borderColor: "border-red",
    gradientFrom: "from-transparent",
    gradientTo: "to-red",
    shadowColor: "shadow-[0px_0px_0px_8px_rgba(192,22,16,0.3)]",
  },
};

// Map backend division types to frontend service IDs and themes
const divisionTypeMapping: Record<
  DivisionType,
  { id: string; name: string; theme: Service["theme"] }
> = {
  COUNSELLING: { id: "counseling", name: "Counselling", theme: "counseling" },
  LIFE_COACHING: {
    id: "life-coaching",
    name: "Life Coaching",
    theme: "lifeCoaching",
  },
  OCCUPATIONAL_THERAPY: {
    id: "occupational-therapy",
    name: "Occupational Therapy",
    theme: "occupationalTherapy",
  },
  PSYCHOLOGY: { id: "psychology", name: "Psychology", theme: "psychology" },
  ALLIED_HEALTH: {
    id: "allied-health",
    name: "Allied Health",
    theme: "occupationalTherapy",
  },
};

// Convert backend division to frontend service
const divisionToService = (division: TDivision): Service => {
  const mapping = divisionTypeMapping[division.type] || {
    id: division.id,
    name: division.type,
    theme: "counseling" as const,
  };

  return {
    id: mapping.id,
    name: mapping.name,
    description:
      division.description ||
      "Lifetime access to the 12-week self-discovery course, including:",
    theme: mapping.theme,
    divisionId: division.id,
    divisionType: division.type,
  };
};

const defaultServices: Service[] = [
  {
    id: "counseling",
    name: "Counselling",
    description:
      "Lifetime access to the 12-week self-discovery course, including:",
    theme: "counseling",
  },
  {
    id: "life-coaching",
    name: "Life Coaching",
    description:
      "Lifetime access to the 12-week self-discovery course, including:",
    theme: "lifeCoaching",
  },
  {
    id: "occupational-therapy",
    name: "Occupational Therapy",
    description:
      "Lifetime access to the 12-week self-discovery course, including:",
    theme: "occupationalTherapy",
  },
  {
    id: "psychology",
    name: "Psychology",
    description:
      "Lifetime access to the 12-week self-discovery course, including:",
    theme: "psychology",
  },
];

const orderedServiceImages = [
  "/images/v3/booking/serviceSelection/booking-1.png",
  "/images/v3/booking/serviceSelection/booking-2.png",
  "/images/v3/booking/serviceSelection/booking-3.png",
  "/images/v3/booking/serviceSelection/booking-4.png",
];

export const ServiceCard = ({
  service,
  isSelected,
  onSelect,
}: {
  service: Service;
  isSelected: boolean;
  onSelect: () => void;
}) => {
  const config = themeConfig[service.theme];
  const imageSrc = service.image || "/images/v3/booking/serviceSelection/booking-1.png";

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={isSelected}
      aria-label={`Select ${service.name} service`}
      className={`
        group relative w-full min-w-0 aspect-[364/476] overflow-hidden rounded-[10px] border-[3px]
        cursor-pointer transition-all duration-300 ease-out
        focus:outline-none focus-visible:ring-2 focus-visible:ring-v3-primary focus-visible:ring-offset-2
        active:scale-[0.97]
        sm:min-w-[200px] sm:max-w-[280px] md:min-w-[280px] md:max-w-[364px] sm:flex-1 h-[100px] sm:h-auto
        ${config.borderColor}
        ${isSelected
          ? `${config.shadowColor} scale-[1.03]`
          : "hover:scale-[1.02] hover:shadow-lg"
        }
      `}
    >
      {/* Background Image */}
      <div className={`absolute inset-0 transition-transform duration-300 ${isSelected ? "scale-105" : "group-hover:scale-105"}`}>
        <Image
          src={imageSrc}
          alt=""
          fill
          className="object-cover"
          sizes="(max-width: 768px) 100vw, 364px"
        />
      </div>

      {/* Gradient Overlay */}
      <div
        className={`absolute inset-x-0 bottom-0 h-[70%] bg-gradient-to-b ${config.gradientFrom} ${config.gradientTo} transition-opacity duration-300 ${isSelected ? "opacity-90" : "opacity-100"}`}
      />

      {/* Selection Indicator - Checkmark */}
      <div
        className={`absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-white/30 backdrop-blur-sm transition-all duration-1000 md:h-10 md:w-10 ${
          isSelected
            ? "scale-100 opacity-100 animate-[pulse_6s_ease-in-out_infinite]"
            : "scale-75 opacity-0"
        }`}
      >
        <svg
          className="h-4 w-4 text-white md:h-5 md:w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={3}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>

      {/* Content */}
      <div className="absolute inset-x-0 bottom-0 flex flex-col gap-2 p-4 md:gap-2.5 md:p-5">
        <h3 className="font-onest text-lg font-medium leading-[1.1] text-white md:text-2xl">
          {service.name}
        </h3>
        <p className="font-onest text-sm font-light leading-[1.2] text-white/90 md:text-base md:leading-[1.1]">
          {service.description}
        </p>
      </div>
    </button>
  );
};

// Export ServiceCards for use in other components
export const ServiceCards = ({
  services = defaultServices,
  selectedId,
  onSelect,
}: {
  services?: Service[];
  selectedId: string | null;
  onSelect: (service: Service) => void;
}) => (
  <div
    className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-[21px] lg:grid-cols-3 xl:grid-cols-4"
    role="radiogroup"
    aria-label="Available services"
  >
    {services.map((service, index) => {
      const mappedService = {
        ...service,
        image: service.image || orderedServiceImages[index % orderedServiceImages.length],
      };

      return (
        <ServiceCard
          key={service.id}
          service={mappedService}
          isSelected={selectedId === service.id}
          onSelect={() => onSelect(service)}
        />
      );
    })}
  </div>
);

const ServiceSelection = ({
  title = "Choose Your Service",
  subtitle = "Select the service that best matches your needs and explore support for wellbeing, relationships and daily life challenges.",
  services: propServices,
  defaultSelectedId,
  onServiceSelect,
  onContinue,
}: ServiceSelectionProps) => {
  const [selectedId, setSelectedId] = useState<string | null>(
    defaultSelectedId || null
  );

  // Fetch divisions from API
  const { data: divisionsResponse, isLoading, error } = useGetDivisionsQuery({});

  // Convert API divisions to services, or use prop services as fallback
  const services: Service[] = (() => {
    if (propServices && propServices.length > 0) {
      return propServices;
    }

    if (divisionsResponse?.data) {
      const divisions = divisionsResponse.data as TDivision[];
      // Filter active divisions and sort by order
      return divisions
        .filter((d) => d.is_active)
        .sort((a, b) => a.order - b.order)
        .map(divisionToService)
        // Filter out ALLIED_HEALTH as it's a category, not a bookable service
        .filter((s) => s.id !== "allied-health");
    }

    return defaultServices;
  })();

  const handleSelect = (service: Service) => {
    setSelectedId(service.id);
    onServiceSelect?.(service);
  };

  const handleContinue = () => {
    const selectedService = services.find((s) => s.id === selectedId) || null;
    onContinue?.(selectedService);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex w-full flex-col gap-10">
        <div className="flex flex-col gap-2">
          <h2 className="font-crimsonText text-2xl font-bold leading-tight text-v3-secondary sm:text-3xl md:text-[42px] md:leading-none">
            {title}
          </h2>
          <p className="max-w-[565px] font-onest text-base font-normal leading-[1.2] text-v3-secondary md:text-lg md:leading-[1.1]">
            {subtitle}
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-[21px] lg:grid-cols-3 xl:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="aspect-[364/476] animate-pulse rounded-[10px] bg-gray-200"
            />
          ))}
        </div>
      </div>
    );
  }

  // Error state - fall back to default services
  if (error) {
    console.error("Failed to fetch divisions:", error);
  }

  return (
    <div className="flex w-full flex-col gap-10">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <h2 className="font-crimsonText text-2xl font-bold leading-tight text-v3-secondary sm:text-3xl md:text-[42px] md:leading-none">
          {title}
        </h2>
        <p className="max-w-[565px] font-onest text-base font-normal leading-[1.2] text-v3-secondary md:text-lg md:leading-[1.1]">
          {subtitle}
        </p>
      </div>

      {/* Service Cards */}
      <ServiceCards
        services={services}
        selectedId={selectedId}
        onSelect={handleSelect}
      />

      {/* Continue Button */}
      <div className="flex items-center justify-end">
        <CustomButton
          label="Continue"
          theme="blue"
          variant="primary"
          size="lg"
          withIcon
          onClick={handleContinue}
        />
      </div>
    </div>
  );
};

export default ServiceSelection;
